import * as THREE from 'three'
import { XRButton } from 'three/addons/webxr/XRButton.js';
import { OculusHandModel } from 'three/examples/jsm/webxr/OculusHandModel.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import gsap from 'gsap';

let renderer;
let baseComposition;
let containerModel;
let delta;
let fresnelMaterial;
let fresnelMaterialInfoBubbles;

// ========================================
// CONTAINER CONFIGURATION
// ========================================
const CONTAINER_CONFIG = {
    width: 0.5,   // meters
    height: 0.6,  // meters
    depth: 0.28,   // meters
    gravity: -9.8 // m/s²
};

// ========================================
// SUIKA GAME - FRUIT CONFIGURATION
// ========================================
const FRUITS = [
    {
        type: 0,
        name: 'Cherry',
        radius: 0.022, // 0.022,
        color: 0xF6100F,  // Red
        restitution: 0.4,  // Hard fruit - bouncy
    },
    {
        type: 1,
        name: 'Strawberry',
        radius: 0.03,
        color: 0xFF3535,  // Bright Red
        restitution: 0.2,  // Soft fruit - slight bounce
    },
    {
        type: 2,
        name: 'Grape',
        radius: 0.039,
        color: 0x8758E8,  // Purple
        restitution: 0.35,  // Firm fruit - bouncy
    },
    {
        type: 3,
        name: 'Dekopon',
        radius: 0.045,
        color: 0xFFB000,  // Orange
        restitution: 0.25,  // Citrus - moderate bounce
    },
    {
        type: 4,
        name: 'Persimmon',
        radius: 0.055,
        color: 0xFD8715,  // Dark Orange
        restitution: 0.15,  // Soft when ripe - low bounce
    },
    {
        type: 5,
        name: 'Apple',
        radius: 0.065,
        color: 0xF41516,  // Apple Red
        restitution: 0.3,  // Firm fruit - moderate bounce
    },
    {
        type: 6,
        name: 'Pear',
        radius: 0.075,
        color: 0xFEF269,  // Yellow-Green
        restitution: 0.2,  // Softer fruit - slight bounce
    },
    {
        type: 7,
        name: 'Peach',
        radius: 0.084,
        color: 0xFECDC9,  // Peach
        restitution: 0.1,  // Very soft - minimal bounce
    },
    {
        type: 8,
        name: 'Pineapple',
        radius: 0.105,
        color: 0xF6E807,  // Yellow
        restitution: 0.15,  // Dense but has give - low bounce
    },
    {
        type: 9,
        name: 'Melon',
        radius: 0.12,
        color: 0x85CA17,  // Green
        restitution: 0.08,  // Heavy and soft - very low bounce
    },
    {
        type: 10,
        name: 'Watermelon',
        radius: 0.14,
        color: 0x62C810,  // Dark Green
        restitution: 0.05,  // Heaviest, mostly water - almost no bounce
    }
];

// Helper function to get fruit configuration by type
function getFruitConfig(type) {
    return FRUITS[type];
}

// Disable frustum culling on an object and all its descendants
function disableFrustumCulling(object) {
    object.traverse(child => {
        child.frustumCulled = false;
    });
}

// Helper function to get random spawnable fruit (typically first 5 fruits in SUIKA)
function getRandomSpawnableFruit() {
    const spawnableFruits = 5; // First 5 fruits can spawn
    return Math.floor(Math.random() * spawnableFruits);
}

// ========================================
// GLB MODEL LOADING
// ========================================
const fruitModels = new Array(11); // Store loaded models for each fruit type (0-10)
const fruitModelSizes = new Array(11); // Store original bounding sphere radius for each model
let modelsLoaded = false;

// Load all fruit models
const gltfLoader = new GLTFLoader();
let modelsLoadedCount = 0;

function loadFruitModels() {
    console.log('Loading fruit models...');
    
    for (let i = 0; i < 11; i++) {
        gltfLoader.load(
            `/static/fruit_${i}.glb`,
            (gltf) => {
                const model = gltf.scene;
                
                // Center the model's bounding box to origin for consistent positioning
                const box = new THREE.Box3().setFromObject(model);
                const center = box.getCenter(new THREE.Vector3());
                model.position.sub(center);
                
                // Calculate the bounding sphere radius of the original model
                const size = box.getSize(new THREE.Vector3());
                const maxDimension = Math.max(size.x, size.y, size.z);
                fruitModelSizes[i] = maxDimension / 2; // Store the radius
                
                disableFrustumCulling(model);
                fruitModels[i] = model;
                modelsLoadedCount++;
                console.log(`Loaded fruit_${i}.glb (${modelsLoadedCount}/11) - original radius: ${fruitModelSizes[i].toFixed(3)}m`);
                
                if (modelsLoadedCount === 11) {
                    modelsLoaded = true;
                    console.log('All fruit models loaded!');
                    
                    // Update visuals now that models are loaded
                    updateDropSphere();
                    updateNextFruitPreview();
                }
            },
            undefined,
            (error) => {
                console.error(`Error loading fruit_${i}.glb:`, error);
            }
        );
    }
}

// Start loading models immediately
loadFruitModels();

// Physics variables
const spawnCooldown = 500; // 0.5 seconds in milliseconds
let lastSpawnTime = 0;

// Current fruit to spawn (will be randomized)
let currentFruitType = 0;
let currentFruitRadius = FRUITS[0].radius;
// Next fruit to spawn (preview in infoBubble1)
let nextFruitType = getRandomSpawnableFruit();
let nextFruitRadius = FRUITS[nextFruitType].radius;

// ========================================
// SCORING SYSTEM
// ========================================
let score = 0;
let comboCount = 0;
let lastMergeTime = 0;
const COMBO_WINDOW = 2000; // 2 seconds to maintain combo
let lastDisplayedScore = -1; // Cache to prevent unnecessary text geometry recreation

// ========================================
// PARTICLE BURST SYSTEM (for merge feedback)
// ========================================
const PARTICLE_POOL_SIZE = 60; // 6 merges worth (10 particles each)
const PARTICLES_PER_BURST = 10;
const particlePool = [];
const activeParticles = [];

function initializeParticlePool() {
    // Pre-create particles for object pooling
    const particleGeometry = new THREE.SphereGeometry(0.006, 8, 8); // Smaller, more subtle
    
    for (let i = 0; i < PARTICLE_POOL_SIZE; i++) {
        const particleMaterial = new THREE.MeshBasicMaterial({
            color: 0xffffff,
            transparent: true,
            opacity: 1
        });
        const particle = new THREE.Mesh(particleGeometry, particleMaterial);
        particle.visible = false;
        containerModel.add(particle);
        particlePool.push(particle);
    }
    
    console.log(`Particle pool initialized: ${PARTICLE_POOL_SIZE} particles`);
}

function createParticleBurst(worldPosition, fruitColor) {
    // Convert world position to container local space
    const localPos = worldToContainer(worldPosition);
    
    // Get particles from pool
    const burstParticles = [];
    for (let i = 0; i < PARTICLES_PER_BURST; i++) {
        const particle = particlePool.find(p => !p.visible);
        if (!particle) {
            console.warn('Particle pool exhausted!');
            break;
        }
        
        // Set particle color to fruit color
        particle.material.color.setHex(fruitColor);
        particle.material.opacity = 1;
        
        // Position at merge point
        particle.position.set(localPos.x, localPos.y, localPos.z);
        particle.scale.set(1, 1, 1);
        particle.visible = true;
        
        burstParticles.push(particle);
        activeParticles.push(particle);
    }
    
    // Animate particles bursting outward
    burstParticles.forEach((particle, index) => {
        // Create a proper 3D spherical burst pattern
        const horizontalAngle = (Math.PI * 2 * index) / PARTICLES_PER_BURST;
        const randomHorizontalOffset = (Math.random() - 0.5) * 0.5;
        const finalHorizontalAngle = horizontalAngle + randomHorizontalOffset;
        
        // Random vertical angle (from -45° to +45° for natural spread)
        const verticalAngle = (Math.random() - 0.5) * Math.PI * 0.5; // -45° to +45°
        
        // Burst radius (smaller for subtlety)
        const burstRadius = 0.08 + Math.random() * 0.04; // 0.08-0.12m
        
        // Calculate 3D position using spherical coordinates
        const horizontalRadius = Math.cos(verticalAngle) * burstRadius;
        const targetX = localPos.x + Math.cos(finalHorizontalAngle) * horizontalRadius;
        const targetY = localPos.y + Math.sin(verticalAngle) * burstRadius;
        const targetZ = localPos.z + Math.sin(finalHorizontalAngle) * horizontalRadius;
        
        // Animate position, scale, and opacity
        gsap.to(particle.position, {
            x: targetX,
            y: targetY,
            z: targetZ,
            duration: 0.4,
            ease: "power2.out"
        });
        
        gsap.to(particle.scale, {
            x: 0.2,
            y: 0.2,
            z: 0.2,
            duration: 0.4,
            ease: "power2.in"
        });
        
        gsap.to(particle.material, {
            opacity: 0,
            duration: 0.4,
            ease: "power2.in",
            onComplete: () => {
                // Return particle to pool
                particle.visible = false;
                const index = activeParticles.indexOf(particle);
                if (index > -1) {
                    activeParticles.splice(index, 1);
                }
            }
        });
    });
}

// ========================================
// GAME OVER DETECTION
// ========================================
let isGameOver = false;
let dangerLineY = 0; // Will be set by physics worker
const DANGER_TIMEOUT = 3000; // 3 seconds above line = game over
const MIN_COLUMNS_FOR_GAMEOVER = 4; // Need 4 out of 5 columns filled
let columnCoverageStartTime = null; // When did coverage exceed threshold

// Physics worker setup
const physics = new Worker(new URL('./physics.worker.js', import.meta.url), { type: 'module' });

// Error handling for worker
physics.onerror = (error) => {
    console.error('Physics worker error:', error);
};

// Initialize physics after worker is ready
physics.postMessage({ 
    type: 'init', 
    container: { 
        width: CONTAINER_CONFIG.width, 
        height: CONTAINER_CONFIG.height 
    }, 
    gravity: CONTAINER_CONFIG.gravity 
});

// map: physics id -> mesh
const fruitMeshes = new Map();

// Score text mesh reference
let scoreText = null;
let scoreFont = null;

// Function to calculate score for a merge
function calculateMergeScore(resultingFruitType) {
    // Base score: Triangular number formula (n * (n + 1)) / 2
    const baseScore = (resultingFruitType * (resultingFruitType + 1)) / 2;
    
    // Check if we're in a combo window
    const currentTime = Date.now();
    if (currentTime - lastMergeTime <= COMBO_WINDOW) {
        comboCount++;
    } else {
        comboCount = 1;
    }
    lastMergeTime = currentTime;
    
    // Apply combo multiplier
    let multiplier = 1.0;
    if (comboCount === 2) {
        multiplier = 1.5;
    } else if (comboCount === 3) {
        multiplier = 2.0;
    } else if (comboCount >= 4) {
        multiplier = 2.5;
    }
    
    const finalScore = Math.floor(baseScore * multiplier);
    console.log(`Merge: ${FRUITS[resultingFruitType].name} | Base: ${baseScore} | Combo: x${multiplier.toFixed(1)} | Points: +${finalScore}`);
    
    return finalScore;
}

// Function to update score display
function updateScoreDisplay() {
    if (!scoreText || !scoreFont) return;
    
    // Only update if score actually changed
    if (score === lastDisplayedScore) return;
    lastDisplayedScore = score;
    
    // Dispose old geometry
    scoreText.geometry.dispose();
    
    // Create new geometry with updated score
    const scoreString = String(score);
    const newTextGeometry = new TextGeometry(scoreString, {
        font: scoreFont,
        size: 0.03,
        height: 0,
    });
    
    // Center the geometry itself (more reliable than manual calculation)
    newTextGeometry.center();
    
    // Update geometry - position stays at 0,0,0 since geometry is centered
    scoreText.geometry = newTextGeometry;
    scoreText.position.set(0, 0, 0);
}

// Debug physics messages
physics.onmessage = (e) => {
  const msg = e.data;
  if (msg.type === 'initialized') {
    // Store danger line position from physics worker
    dangerLineY = msg.dangerLineY;
    console.log('Danger line set at Y:', dangerLineY);
  } else if (msg.type === 'spawned') {
    const { id, radius, x, y, fruitType } = msg;
    console.log('Spawning fruit:', FRUITS[fruitType].name, 'at:', x, y);
    console.log('Models loaded:', modelsLoaded, 'Model exists:', !!fruitModels[fruitType]);
    
    // Get fruit config
    const fruitConfig = getFruitConfig(fruitType);
    
    let m;
    
    // Use GLB model if loaded, otherwise fallback to sphere
    if (modelsLoaded && fruitModels[fruitType]) {
        console.log('Using GLB model for fruit type:', fruitType);
        // Clone the loaded model
        m = fruitModels[fruitType].clone();
        
        // Scale the model to match the desired radius
        // Scale factor = desired radius / original model radius
        const originalRadius = fruitModelSizes[fruitType];
        const scale = radius / originalRadius;
        m.scale.set(scale, scale, scale);
    } else {
        console.log('Using fallback sphere for fruit type:', fruitType);
        // Fallback: Create sphere with fruit color (reduced segments for mobile VR performance)
        const geo = new THREE.SphereGeometry(radius, 12, 12);
        const mat = new THREE.MeshPhongMaterial({ 
            color: fruitConfig.color,
            shininess: 30,
            specular: 0x222222
        });
        m = new THREE.Mesh(geo, mat);
    }
    
    m.userData.fruitType = fruitType;  // Store fruit type in mesh
    disableFrustumCulling(m);
    
    containerModel.add(m);      // IMPORTANT: parented to container (local space)
    m.position.set(x, y, 0);
    fruitMeshes.set(id, m);
  } else if (msg.type === 'poses') {
    for (const u of msg.updates) {
      const m = fruitMeshes.get(u.id);
      if (m) {
        m.position.set(u.x, u.y, 0);
        // Apply rotation around Z-axis (since we're using 2D physics)
        if (u.rotation !== undefined) {
          m.rotation.z = u.rotation;
        }
      }
    }
    
    // Handle danger line violations with column coverage
    if (!isGameOver && msg.dangerFruits && msg.occupiedColumns) {
      const currentTime = Date.now();
      const numOccupiedColumns = msg.occupiedColumns.length;
      
      // Check if enough columns are occupied to be in danger
      if (numOccupiedColumns >= MIN_COLUMNS_FOR_GAMEOVER) {
        if (columnCoverageStartTime === null) {
          // First time we've exceeded the column threshold
          columnCoverageStartTime = currentTime;
          console.log(`Danger: ${numOccupiedColumns}/${msg.totalColumns} columns occupied (columns: ${msg.occupiedColumns.join(', ')})`);
        } else {
          // Check how long we've been at danger coverage
          const timeInDanger = currentTime - columnCoverageStartTime;
          
          if (timeInDanger >= DANGER_TIMEOUT) {
            // Game Over!
            console.log(`Game Over: ${numOccupiedColumns} columns occupied for ${timeInDanger}ms`);
            triggerGameOver();
          }
        }
      } else {
        // Coverage dropped below threshold - reset timer
        if (columnCoverageStartTime !== null) {
          console.log(`Safe: Only ${numOccupiedColumns}/${msg.totalColumns} columns occupied - timer reset`);
          columnCoverageStartTime = null;
        }
      }
    }
  } else if (msg.type === 'merge') {
    // Handle fruit merging - remove merged fruit meshes
    for (const id of msg.removedIds) {
      const mesh = fruitMeshes.get(id);
      if (mesh) {
        // Get world position for particle burst before removing
        const worldPos = new THREE.Vector3();
        mesh.getWorldPosition(worldPos);
        
        // Get fruit color for particles
        const fruitType = mesh.userData.fruitType;
        const fruitColor = FRUITS[fruitType]?.color || 0xffffff;
        
        // Trigger particle burst at merge location
        createParticleBurst(worldPos, fruitColor);
        
        // Quick scale-up "pop" animation before disappearing
        const currentScale = mesh.scale.x;
        gsap.to(mesh.scale, {
          x: currentScale * 1.3,
          y: currentScale * 1.3,
          z: currentScale * 1.3,
          duration: 0.1,
          ease: "power2.out",
          onComplete: () => {
            // Remove from scene after animation
            containerModel.remove(mesh);
            
            // Dispose geometry and material to free memory
            // Handle both simple meshes and complex GLB models
            mesh.traverse((child) => {
              if (child.geometry) child.geometry.dispose();
              if (child.material) {
                if (Array.isArray(child.material)) {
                  child.material.forEach(mat => mat.dispose());
                } else {
                  child.material.dispose();
                }
              }
            });
          }
        });
        
        // Remove from tracking map immediately
        fruitMeshes.delete(id);
        console.log('Removed merged fruit:', id);
      }
    }
    
    // Calculate and add score from merge events
    if (msg.mergeEvents && msg.mergeEvents.length > 0) {
      for (const mergeEvent of msg.mergeEvents) {
        const points = calculateMergeScore(mergeEvent.resultingFruitType);
        score += points;
      }
      updateScoreDisplay();
    }
  }
};

// Sphere movement variables
let leftHandPinchActive = false;
let rightHandPinchActive = false;
let lastRightHandX = 0;
let lastLeftHandX = 0;
let sphereMovementSpeed = 1.0;

// Button press variables
let buttonPressed = false;
let buttonPressedByLeftIndex = false;
let buttonPressedByRightIndex = false;
let lastButtonPressTime = 0;
const BUTTON_COOLDOWN = 1000; // 1 second cooldown between presses

// Scene
const scene = new THREE.Scene()
scene.background = new THREE.Color(0x505050)

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Camera
const camera = new THREE.PerspectiveCamera(50, window.innerWidth / window.innerHeight, 0.1, 50);
camera.position.set(0, 1.6, 3);
scene.add(camera)

// Renderer
renderer = new THREE.WebGLRenderer({ 
    canvas: canvas,
    antialias: true 
});
// Cap pixel ratio for mobile VR performance (prevents rendering 4-9x more pixels)
// Mobile VR headsets often have pixelRatio of 2-3, which kills performance
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.0));
renderer.setSize(window.innerWidth, window.innerHeight);
renderer.xr.enabled = true;
// No need to append - already using the canvas from HTML
// Create XR button but hide it (we'll use the whole page instead)
const xrButton = XRButton.createButton(renderer, { 'optionalFeatures': ['hand-tracking'] });
xrButton.style.display = 'none'; // Hide the default button
document.body.appendChild(xrButton);

// Get error message element
const errorMessage = document.getElementById('webxr-error');

// Check WebXR support on page load
let webxrSupported = false;
if (navigator.xr) {
    navigator.xr.isSessionSupported('immersive-vr').then((supported) => {
        webxrSupported = supported;
        if (!supported) {
            console.log('WebXR immersive-vr not supported on this device/browser');
        }
    }).catch((error) => {
        console.error('Error checking WebXR support:', error);
        webxrSupported = false;
    });
} else {
    console.log('navigator.xr not available');
}

// Make the entire page clickable for entering WebXR
document.body.style.cursor = 'pointer';
document.body.addEventListener('click', () => {
    // Check if WebXR is supported (synchronously, since we checked on load)
    if (!navigator.xr || !webxrSupported) {
        // WebXR not supported, show error message
        showWebXRError();
    } else {
        // WebXR is supported, proceed with session
        // Simply click the hidden XR button to trigger WebXR session
        // This uses the same logic as the original XRButton
        xrButton.click();
    }
});

// Function to show WebXR error message
function showWebXRError() {
    if (errorMessage) {
        errorMessage.style.display = 'block';
        // Hide the "Start WebXR" text
        document.body.classList.add('webxr-error');
        document.body.style.cursor = 'default';
    }
}

// Add visual feedback for the clickable area
document.body.style.transition = 'background-color 0.3s ease';
document.body.addEventListener('mouseenter', () => {
    document.body.style.backgroundColor = 'rgba(0, 0, 0, 0.1)';
});
document.body.addEventListener('mouseleave', () => {
    document.body.style.backgroundColor = '';
});

// Track XR session state to hide/show the button
renderer.xr.addEventListener('sessionstart', () => {
    document.body.classList.add('xr-presenting');
});

renderer.xr.addEventListener('sessionend', () => {
    document.body.classList.remove('xr-presenting');
});

// Base Composition (container for the box)
const tempGeo = new THREE.BoxGeometry(0.3, 0.3, 0.3);
const tempMat = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.0,
    depthTest: false,
    depthWrite: false
});
baseComposition = new THREE.Mesh(tempGeo, tempMat);
baseComposition.scale.set(1, 1, 1);
scene.add(baseComposition);

// Auto-position composition at user's head height
let compositionPositioned = false;
let xrSessionStartTime = null;

function positionCompositionAtHeadHeight() {
    if (compositionPositioned || !xrSessionStartTime) return;

    // Wait 1 second after WebXR session starts
    const elapsedTime = Date.now() - xrSessionStartTime;
    if (elapsedTime < 1000) {
        return;
    }

    // Get user's head position (camera position)
    const headHeight = camera.position.y;
    const headDistance = camera.position.z;

    // Position composition below head height, slightly in front of user
    baseComposition.position.set(0, headHeight - 0.2, headDistance - 1.85);

    compositionPositioned = true;
}

renderer.xr.addEventListener('sessionstart', () => {
    xrSessionStartTime = Date.now();
    gameStartTime = Date.now(); // Start the timer
    compositionPositioned = false;
    
    // Disable frustum culling on all objects already in the scene
    disableFrustumCulling(scene);
    
    // Reset game state
    isGameOver = false;
    columnCoverageStartTime = null;
    score = 0;
    comboCount = 0;
    lastMergeTime = 0;
    lastDisplayedScore = -1; // Reset cache to force display update
    lastDisplayedTime = ''; // Reset cache to force display update
    updateScoreDisplay();
    
    // Update visuals to use GLB models if they're loaded
    if (modelsLoaded) {
        updateDropSphere();
        updateNextFruitPreview();
    }
});

renderer.xr.addEventListener('sessionend', () => {
    xrSessionStartTime = null;
    gameStartTime = null; // Reset timer
    compositionPositioned = false; // Reset for next session
});

// Helper function to load shader files
async function loadShader(url) {
    const response = await fetch(url);
    return await response.text();
}

// Helper function to apply Fresnel shader to container GLB
function applyFresnelShaderToContainer() {
    if (containerGLB && fresnelMaterial) {
        containerGLB.traverse((child) => {
            if (child.isMesh) {
                child.material = fresnelMaterial;
            }
        });
        console.log('Fresnel shader applied to container!');
    }
}

// Helper function to apply Fresnel shader to infoBubbles
function applyFresnelShaderToInfoBubbles() {
    if (fresnelMaterialInfoBubbles && infoBubble0 && infoBubble1 && infoBubble2) {
        infoBubble0.material = fresnelMaterialInfoBubbles;
        infoBubble1.material = fresnelMaterialInfoBubbles;
        infoBubble2.material = fresnelMaterialInfoBubbles;
        console.log('Fresnel shader applied to infoBubbles!');
    }
}

// Load Fresnel shaders and create material
let fresnelVertexShader, fresnelFragmentShader;
Promise.all([
    loadShader('/static/shaders/fresnel_vertex.glsl'),
    loadShader('/static/shaders/fresnel_fragment.glsl')
]).then(([vertexShader, fragmentShader]) => {
    fresnelVertexShader = vertexShader;
    fresnelFragmentShader = fragmentShader;
    
    // Create Fresnel shader material for container
    fresnelMaterial = new THREE.ShaderMaterial({
        vertexShader: fresnelVertexShader,
        fragmentShader: fresnelFragmentShader,
        uniforms: {
            outlineThickness: { value: 0.8 },  // Softer, more subtle glow
            outlineColor: { value: new THREE.Color(0xF7FBFF) },  // Very light blue/white color
            opacity: { value: 0.1 }  // 0.0 = fully transparent, 1.0 = full effect
        },
        side: THREE.DoubleSide,
        transparent: true,
        depthWrite: false,
    });
    
    // Create separate Fresnel shader material for infoBubbles with higher opacity
    fresnelMaterialInfoBubbles = new THREE.ShaderMaterial({
        vertexShader: fresnelVertexShader,
        fragmentShader: fresnelFragmentShader,
        uniforms: {
            outlineThickness: { value: 1.5 },  // Thick, diffuse glow for infoBubbles
            outlineColor: { value: new THREE.Color(0xF7FBFF) },  // Very light blue/white color
            opacity: { value: 0.2 }  // Higher opacity for infoBubbles
        },
        side: THREE.DoubleSide,
        transparent: true,
        depthWrite: false,
    });
    
    console.log('Fresnel shader loaded successfully!');
    
    // Apply shader to container if it's already loaded
    applyFresnelShaderToContainer();
    
    // Apply shader to infoBubbles
    applyFresnelShaderToInfoBubbles();
}).catch(error => {
    console.error('Error loading Fresnel shaders:', error);
});

// Create a container (invisible - size set to 0 to avoid z-fighting)
const containerGeometry = new THREE.BoxGeometry(0, 0, 0);
const containerMaterial = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.0
});
containerModel = new THREE.Mesh(containerGeometry, containerMaterial);

// Load suika-container.glb and add as child
let containerGLB = null;
gltfLoader.load(
    '/static/suika-container.glb',
    (gltf) => {
        containerGLB = gltf.scene;
        
        // Center the model at origin
        const box = new THREE.Box3().setFromObject(containerGLB);
        const center = box.getCenter(new THREE.Vector3());
        containerGLB.position.sub(center);
        
        // Set position to origin
        containerGLB.position.set(0, 0, 0);
        
        // Scale down to 50%
        containerGLB.scale.set(0.5, 0.5, 0.5);
        
        // Add as child of containerModel
        disableFrustumCulling(containerGLB);
        containerModel.add(containerGLB);
        console.log('Suika container GLB loaded successfully!');
        
        // Apply Fresnel shader if it's already loaded
        applyFresnelShaderToContainer();
    },
    undefined,
    (error) => {
        console.error('Error loading suika-container.glb:', error);
    }
);

// Add a sphere as child of containerModel with same transparent material (reduced segments for mobile VR)
const sphereGeometry = new THREE.SphereGeometry(0.08, 16, 16);
const sphereMaterial = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    transparent: true,
    opacity: 0.15
});
const infoBubble1 = new THREE.Mesh(sphereGeometry, sphereMaterial);
infoBubble1.position.y = 0.55; // Move up on Y axis
containerModel.add(infoBubble1);

// Add preview sphere for next fruit inside infoBubble1
let nextFruitPreview;

function createNextFruitPreview() {
    // Remove old preview if it exists
    if (nextFruitPreview) {
        infoBubble1.remove(nextFruitPreview);
        
        // Properly dispose (handles both spheres and GLB models)
        nextFruitPreview.traverse((child) => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
                if (Array.isArray(child.material)) {
                    child.material.forEach(mat => mat.dispose());
                } else {
                    child.material.dispose();
                }
            }
        });
    }
    
    // Use GLB model if loaded, otherwise fallback to sphere
    if (modelsLoaded && fruitModels[nextFruitType]) {
        nextFruitPreview = fruitModels[nextFruitType].clone();
        const originalRadius = fruitModelSizes[nextFruitType];
        const scale = (nextFruitRadius / originalRadius) * 0.8; // 80% size for preview
        nextFruitPreview.scale.set(scale, scale, scale);
    } else {
        // Fallback: sphere (reduced segments for mobile VR performance)
        const nextFruitPreviewGeometry = new THREE.SphereGeometry(nextFruitRadius, 12, 12);
        const nextFruitPreviewMaterial = new THREE.MeshPhongMaterial({ 
            color: FRUITS[nextFruitType].color,
            shininess: 30,
            specular: 0x222222
        });
        nextFruitPreview = new THREE.Mesh(nextFruitPreviewGeometry, nextFruitPreviewMaterial);
        nextFruitPreview.scale.set(0.8, 0.8, 0.8); // 80% size for preview
    }
    
    nextFruitPreview.position.set(0, 0, 0); // Centered in the bubble
    disableFrustumCulling(nextFruitPreview);
    infoBubble1.add(nextFruitPreview);
    
    return nextFruitPreview;
}

// Create initial preview
nextFruitPreview = createNextFruitPreview();

const infoBubble0 = new THREE.Mesh(sphereGeometry, sphereMaterial);
infoBubble0.position.set(-0.18, 0.55, 0);
containerModel.add(infoBubble0);

// Add text to infoBubble0 - Score Display
const fontLoader = new FontLoader();
fontLoader.load('/static/fonts/nunito-extrabold.json', function(font) {
    scoreFont = font; // Store font for updates
    
    const textGeometry = new TextGeometry('0', {
        font: font,
        size: 0.03,
        height: 0,
    });
    
    // Center the geometry itself
    textGeometry.center();
    
    const textMaterial = new THREE.MeshBasicMaterial({ color: 0xFFF000 });
    scoreText = new THREE.Mesh(textGeometry, textMaterial);
    
    // Position at center of bubble (0,0,0 relative to infoBubble0)
    scoreText.position.set(0, 0, 0);
    
    infoBubble0.add(scoreText);
});

const infoBubble2 = new THREE.Mesh(sphereGeometry, sphereMaterial);
infoBubble2.position.set(0.18, 0.55, 0);
containerModel.add(infoBubble2);

// Apply Fresnel shader to infoBubbles if it's already loaded
applyFresnelShaderToInfoBubbles();

// Time tracking variables
let timeText = null;
let timeFont = null;
let gameStartTime = null;
let elapsedSeconds = 0;
let lastDisplayedTime = ''; // Cache to prevent unnecessary text geometry recreation

// Add text to infoBubble2
fontLoader.load('/static/fonts/nunito-extrabold.json', function(font) {
    timeFont = font; // Store font for updates
    
    const textGeometry = new TextGeometry('00:00', {
        font: font,
        size: 0.03,
        height: 0,
    });
    
    // Center the text
    textGeometry.computeBoundingBox();
    const textWidth = textGeometry.boundingBox.max.x - textGeometry.boundingBox.min.x;
    const textHeight = textGeometry.boundingBox.max.y - textGeometry.boundingBox.min.y;
    
    const textMaterial = new THREE.MeshBasicMaterial({ color: 0xFFF000 });
    timeText = new THREE.Mesh(textGeometry, textMaterial);
    
    // Position text at center of bubble
    timeText.position.set(-textWidth / 2, -textHeight / 2, 0);
    
    infoBubble2.add(timeText);
});

// Container dimensions and helpers
const halfW = CONTAINER_CONFIG.width * 0.5;

// world <-> container local converters
function worldToContainer(p) {
  const inv = new THREE.Matrix4().copy(containerModel.matrixWorld).invert();
  return p.clone().applyMatrix4(inv);
}
function containerToWorld(p) {
  return p.clone().applyMatrix4(containerModel.matrixWorld);
}

// Add danger line visualization (94% of container height) - hidden
const dangerLineHeight = -CONTAINER_CONFIG.height * 0.5 + (CONTAINER_CONFIG.height * 0.94);
const dangerLineGeometry = new THREE.PlaneGeometry(CONTAINER_CONFIG.width * 0.95, 0.002);
const dangerLineMaterial = new THREE.MeshBasicMaterial({ 
    color: 0xff4444,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.5
});
const dangerLineMesh = new THREE.Mesh(dangerLineGeometry, dangerLineMaterial);
dangerLineMesh.position.set(0, dangerLineHeight, 0);
dangerLineMesh.visible = false; // Hidden
containerModel.add(dangerLineMesh);

// Add endPanel facing the player
// Aspect ratio 418:360 = 1.161111
const endPanelWidth = 0.33; // 110% of original (0.3 * 1.1)
const endPanelHeight = endPanelWidth * (360 / 418); // Maintain 418:360 aspect ratio
const endPanelGeometry = new THREE.PlaneGeometry(endPanelWidth, endPanelHeight);

// Load UI_PANEL-REF.png as texture
const textureLoader = new THREE.TextureLoader();
const endPanelTexture = textureLoader.load('/static/UI_PANEL-REF.png');

const endPanelMaterial = new THREE.MeshBasicMaterial({ 
    map: endPanelTexture,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 1.0
});
const endPanel = new THREE.Mesh(endPanelGeometry, endPanelMaterial);
endPanel.position.set(0, 0.05, 1.0); // Default position (will be updated on game over)
endPanel.scale.set(0, 0, 0); // Initially invisible
endPanel.renderOrder = 1; // Render panel first
scene.add(endPanel); // Add to scene instead of containerModel for independent positioning

// Add child plane to endPanel
// Aspect ratio 218:76 = 2.868421
const buttonWidth = 0.18; // 90% of original size (0.2 * 0.9)
const buttonHeight = buttonWidth * (76 / 218); // Maintain 218:76 aspect ratio
const buttonPlaceholderGeometry = new THREE.PlaneGeometry(buttonWidth, buttonHeight);

// Load UI_BUTTON.png as texture
const buttonTexture = textureLoader.load('/static/UI_BUTTON.png');

const buttonPlaceholderMaterial = new THREE.MeshBasicMaterial({ 
    map: buttonTexture,
    side: THREE.DoubleSide,
    transparent: true,
    depthTest: false,
    depthWrite: false
});
const buttonPlaceholder = new THREE.Mesh(buttonPlaceholderGeometry, buttonPlaceholderMaterial);
buttonPlaceholder.position.set(0, -0.082, 0.02); // Shifted in z to avoid z-fighting
buttonPlaceholder.renderOrder = 2; // Render button on top of panel
endPanel.add(buttonPlaceholder);

// Add "Game Over" text to endPanel
let gameOverText = null;
fontLoader.load('/static/fonts/nunito-extrabold.json', function(font) {
    const textGeometry = new TextGeometry('Game Over', {
        font: font,
        size: 0.035,
        height: 0,
    });
    
    // Center the geometry itself
    textGeometry.center();
    
    const textMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
    gameOverText = new THREE.Mesh(textGeometry, textMaterial);
    
    // Position above the score
    gameOverText.position.set(0, 0.095, 0.005);
    gameOverText.renderOrder = 3; // Render text on top
    
    endPanel.add(gameOverText);
});

// Add final score text to endPanel
let endPanelScoreText = null;
fontLoader.load('/static/fonts/nunito-extrabold.json', function(font) {
    const textGeometry = new TextGeometry('0', {
        font: font,
        size: 0.045,
        height: 0,
    });
    
    // Center the geometry itself
    textGeometry.center();
    
    const textMaterial = new THREE.MeshBasicMaterial({ color: 0xFFF000 });
    endPanelScoreText = new THREE.Mesh(textGeometry, textMaterial);
    
    // Position at center of panel, shifted slightly in Z to avoid z-fighting
    endPanelScoreText.position.set(0, 0.025, 0.005);
    endPanelScoreText.renderOrder = 3; // Render score text on top
    
    endPanel.add(endPanelScoreText);
});

// Add "points" label text below the dynamic score
let scoreLabelText = null;
fontLoader.load('/static/fonts/nunito-extrabold.json', function(font) {
    const textGeometry = new TextGeometry('points', {
        font: font,
        size: 0.0175,
        height: 0,
    });
    
    // Center the geometry itself
    textGeometry.center();
    
    const textMaterial = new THREE.MeshBasicMaterial({ color: 0xffffff });
    scoreLabelText = new THREE.Mesh(textGeometry, textMaterial);
    
    // Position below the dynamic score
    scoreLabelText.position.set(0, -0.02, 0.005);
    scoreLabelText.renderOrder = 3; // Render text on top
    
    endPanel.add(scoreLabelText);
});

// Add a drop sphere that shows the next fruit to spawn
// Will be replaced with model when loaded
let dropSphere;
let verticalLine; // Independent vertical guide line

function createDropSphere() {
    // Remove old dropSphere if it exists
    if (dropSphere) {
        containerModel.remove(dropSphere);
        
        // Properly dispose (handles both spheres and GLB models)
        dropSphere.traverse((child) => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
                if (Array.isArray(child.material)) {
                    child.material.forEach(mat => mat.dispose());
                } else {
                    child.material.dispose();
                }
            }
        });
    }
    
    // Use GLB model if loaded, otherwise fallback to sphere
    if (modelsLoaded && fruitModels[currentFruitType]) {
        dropSphere = fruitModels[currentFruitType].clone();
        const originalRadius = fruitModelSizes[currentFruitType];
        const scale = currentFruitRadius / originalRadius;
        console.log(`DropSphere - Type: ${currentFruitType}, Original radius: ${originalRadius}, Desired radius: ${currentFruitRadius}, Scale: ${scale}`);
        dropSphere.scale.set(scale, scale, scale);
    } else {
        // Fallback: sphere (reduced segments for mobile VR performance)
        const dropSphereGeometry = new THREE.SphereGeometry(currentFruitRadius, 12, 12);
        const dropSphereMaterial = new THREE.MeshPhongMaterial({ 
            color: FRUITS[currentFruitType].color,
            shininess: 30,
            specular: 0x222222
        });
        dropSphere = new THREE.Mesh(dropSphereGeometry, dropSphereMaterial);
    }
    
    // Position the drop sphere above the container (slightly above the top edge)
    const dropSphereY = (CONTAINER_CONFIG.height * 0.5) + 0.05; // 5cm above top edge
    dropSphere.position.set(0, dropSphereY, 0);
    disableFrustumCulling(dropSphere);
    
    return dropSphere;
}

// Create initial dropSphere
dropSphere = createDropSphere();

containerModel.add(dropSphere);

// Create vertical guide line (independent of dropSphere)
const lineHeight = 0.645;
const lineGeometry = new THREE.PlaneGeometry(0.0035, lineHeight);
const lineMaterial = new THREE.MeshBasicMaterial({
    color: 0xffffff,
    side: THREE.DoubleSide,
    transparent: true,
    opacity: 0.6
});
verticalLine = new THREE.Mesh(lineGeometry, lineMaterial);

// Position line to follow dropSphere
const dropSphereY = (CONTAINER_CONFIG.height * 0.5) + 0.05;
verticalLine.position.set(0, dropSphereY - lineHeight / 2, 0);

containerModel.add(verticalLine);

// Function to update drop sphere appearance
function updateDropSphere() {
    // Store current visibility and position
    const wasVisible = dropSphere.visible;
    const currentX = dropSphere.position.x;
    const currentY = dropSphere.position.y;
    
    // Recreate dropSphere with new fruit type
    const oldDropSphere = dropSphere;
    dropSphere = createDropSphere();
    
    // Remove old one from container if it exists
    if (oldDropSphere) {
        containerModel.remove(oldDropSphere);
    }
    
    // Restore visibility and position
    dropSphere.visible = wasVisible;
    dropSphere.position.set(currentX, currentY, 0);
    
    // Add to container
    containerModel.add(dropSphere);
}

// Function to update next fruit preview in infoBubble1
function updateNextFruitPreview() {
    // Recreate preview with new fruit type
    nextFruitPreview = createNextFruitPreview();
}

// Function to trigger game over
function triggerGameOver() {
    if (isGameOver) return; // Already game over
    
    isGameOver = true;
    console.log('=== GAME OVER ===');
    console.log('Final Score:', score);
    console.log('Time:', elapsedSeconds, 'seconds');
    
    // Reset coverage timer
    columnCoverageStartTime = null;
    
    // Hide dropSphere and vertical line
    dropSphere.visible = false;
    verticalLine.visible = false;
    
    // Position endPanel relative to user's head (camera)
    const headHeight = camera.position.y;
    const headDistance = camera.position.z;
    
    // Position panel: 0.2m below head height, 1.1m in front
    endPanel.position.set(0, headHeight - 0.2, headDistance - 1.1);
    
    // Make panel face the camera horizontally (Y-axis only), keeping it vertical
    // Calculate horizontal direction from panel to camera
    const horizontalDirection = new THREE.Vector3(
        camera.position.x - endPanel.position.x,
        0, // Keep Y at 0 for horizontal rotation only
        camera.position.z - endPanel.position.z
    ).normalize();
    
    // Calculate rotation angle around Y-axis
    const angle = Math.atan2(horizontalDirection.x, horizontalDirection.z);
    endPanel.rotation.set(0, angle, 0);
    
    // Update endPanel score text with final score
    if (endPanelScoreText && scoreFont) {
        endPanelScoreText.geometry.dispose();
        
        const finalScoreString = String(score);
        const newTextGeometry = new TextGeometry(finalScoreString, {
            font: scoreFont,
            size: 0.045,
            height: 0,
        });
        
        // Center the geometry
        newTextGeometry.center();
        
        // Update geometry
        endPanelScoreText.geometry = newTextGeometry;
        endPanelScoreText.position.set(0, 0.025, 0.005);
    }
    
    // Animate endPanel scale up
    gsap.to(endPanel.scale, {
        x: 1,
        y: 1,
        z: 1,
        duration: 0.3,
        ease: "back.out(1.7)"
    });
}

// Function to update time display
function updateTimeDisplay() {
    if (!timeText || !timeFont || !gameStartTime || isGameOver) return;
    
    // Calculate elapsed time in seconds
    const currentTime = Date.now();
    elapsedSeconds = Math.floor((currentTime - gameStartTime) / 1000);
    
    // Convert to minutes and seconds
    const minutes = Math.floor(elapsedSeconds / 60);
    const seconds = elapsedSeconds % 60;
    
    // Format as MM:SS with leading zeros
    const timeString = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    
    // Only update if time string changed (once per second instead of 72+ times per second)
    if (timeString === lastDisplayedTime) return;
    lastDisplayedTime = timeString;
    
    // Dispose old geometry
    timeText.geometry.dispose();
    
    // Create new geometry with updated time
    const newTextGeometry = new TextGeometry(timeString, {
        font: timeFont,
        size: 0.03,
        height: 0,
    });
    
    // Center the text
    newTextGeometry.computeBoundingBox();
    const textWidth = newTextGeometry.boundingBox.max.x - newTextGeometry.boundingBox.min.x;
    const textHeight = newTextGeometry.boundingBox.max.y - newTextGeometry.boundingBox.min.y;
    
    // Update geometry and position
    timeText.geometry = newTextGeometry;
    timeText.position.set(-textWidth / 2, -textHeight / 2, 0);
}

baseComposition.add(containerModel);

// Initialize particle pool for merge effects
initializeParticlePool();

/**
 * Light
 */
const ambientLight = new THREE.AmbientLight(0xffffff, 1.0)
scene.add(ambientLight)

// Add directional light for better 3D shading
const directionalLight = new THREE.DirectionalLight(0xffffff, 2.5)
directionalLight.position.set(2, 3, 5)
scene.add(directionalLight)

// Add cloud model that follows dropSphere
let cloudPlaceholder = null;

// Fresnel shader material for cloud (no transparency)
const fresnelCloudMaterial = new THREE.ShaderMaterial({
    uniforms: {
        fresnelColor: { value: new THREE.Color(0xffffff) },
        rimColor: { value: new THREE.Color(0x88ccff) },
        fresnelPower: { value: 0.6 },
        rimStrength: { value: 0.6 }
    },
    vertexShader: `
        varying vec3 vNormal;
        varying vec3 vViewPosition;
        
        void main() {
            vNormal = normalize(normalMatrix * normal);
            vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
            vViewPosition = -mvPosition.xyz;
            gl_Position = projectionMatrix * mvPosition;
        }
    `,
    fragmentShader: `
        uniform vec3 fresnelColor;
        uniform vec3 rimColor;
        uniform float fresnelPower;
        uniform float rimStrength;
        
        varying vec3 vNormal;
        varying vec3 vViewPosition;
        
        void main() {
            vec3 normal = normalize(vNormal);
            vec3 viewDir = normalize(vViewPosition);
            
            // Fresnel effect
            float fresnel = pow(1.0 - abs(dot(normal, viewDir)), fresnelPower);
            
            // Mix base color with rim color based on fresnel
            vec3 finalColor = mix(fresnelColor, rimColor, fresnel * rimStrength);
            
            gl_FragColor = vec4(finalColor, 1.0);
        }
    `,
    side: THREE.DoubleSide
});

gltfLoader.load(
    '/static/cloud.glb',
    (gltf) => {
        cloudPlaceholder = gltf.scene;
        
        // Apply Fresnel shader ONLY to the first mesh, keep rest intact
        let firstMeshFound = false;
        cloudPlaceholder.traverse((child) => {
            if (child.isMesh && !firstMeshFound) {
                child.material = fresnelCloudMaterial;
                firstMeshFound = true;
                console.log('Applied Fresnel shader to first mesh:', child.name);
            }
        });
        
        cloudPlaceholder.scale.set(0.05, 0.05, 0.05);
        cloudPlaceholder.position.set(0, 0.41, -0.06);
        disableFrustumCulling(cloudPlaceholder);
        baseComposition.add(cloudPlaceholder);
        console.log('Cloud model loaded successfully with Fresnel shader on first mesh only');
    },
    undefined,
    (error) => {
        console.error('Error loading cloud model:', error);
        // Fallback to placeholder sphere if model fails to load (reduced segments for mobile VR)
        const cloudPlaceholderGeometry = new THREE.SphereGeometry(0.05, 16, 16);
        cloudPlaceholder = new THREE.Mesh(cloudPlaceholderGeometry, fresnelCloudMaterial);
        cloudPlaceholder.position.set(0, 0.41, -0.06);
        baseComposition.add(cloudPlaceholder);
    }
);

/**
 * Joint References
 */
// Invisible hand tracking spheres - size set to 0 to prevent any rendering artifacts
const sphere = new THREE.SphereGeometry(0, 0, 0)
const defaultMat = new THREE.MeshBasicMaterial({ 
    color: 0xff0000, 
    opacity: 0,
    transparent: true 
})

const lThumbObj = new THREE.Mesh(sphere, defaultMat)
const lIndexObj = new THREE.Mesh(sphere, defaultMat)
const rThumbObj = new THREE.Mesh(sphere, defaultMat)
const rIndexObj = new THREE.Mesh(sphere, defaultMat)

scene.add(lThumbObj)
scene.add(lIndexObj)
scene.add(rThumbObj)
scene.add(rIndexObj)

/**
 * Pinch References
 */
// Small visible pinch spheres - reduced segments for mobile VR performance
const sphere2 = new THREE.SphereGeometry(0.006, 12, 12)
const defaultMat2 = new THREE.MeshLambertMaterial({ 
    color: 0xffffff,
})
const greenMat2 = new THREE.MeshLambertMaterial({ 
    color: 0x00ff00,
})

const lPinchSphere = new THREE.Mesh(sphere2, defaultMat2)
const rPinchSphere = new THREE.Mesh(sphere2, greenMat2)
scene.add(lPinchSphere)
scene.add(rPinchSphere)
lPinchSphere.visible = false;
rPinchSphere.visible = false;

/**
 * Hands
 */
var hand1, hand2;

hand1 = renderer.xr.getHand(0);
hand1.add(new OculusHandModel(hand1));
scene.add(hand1);

hand2 = renderer.xr.getHand(1);
hand2.add(new OculusHandModel(hand2));
scene.add(hand2);

const handsGroup = new THREE.Group();
handsGroup.add(hand1, hand2);
scene.add(handsGroup);
handsGroup.visible = false;

const clock = new THREE.Clock();

function handTracking() {
    // Force hand1 to always be left hand tracking, hand2 to be right
    const leftHand = hand1;
    const rightHand = hand2;
    
    // Left Hand Joint References
    if (leftHand && leftHand.joints && leftHand.joints['thumb-tip']) {
        lThumbObj.position.x = leftHand.joints['thumb-tip'].position.x;
        lThumbObj.position.y = leftHand.joints['thumb-tip'].position.y;
        lThumbObj.position.z = leftHand.joints['thumb-tip'].position.z;
        lThumbObj.rotation.x = leftHand.joints['thumb-tip'].rotation.x;
        lThumbObj.rotation.y = leftHand.joints['thumb-tip'].rotation.y;
        lThumbObj.rotation.z = leftHand.joints['thumb-tip'].rotation.z;
    }

    if (leftHand && leftHand.joints && leftHand.joints['index-finger-tip']) {
        lIndexObj.position.x = leftHand.joints['index-finger-tip'].position.x;
        lIndexObj.position.y = leftHand.joints['index-finger-tip'].position.y;
        lIndexObj.position.z = leftHand.joints['index-finger-tip'].position.z;
        lIndexObj.rotation.x = leftHand.joints['index-finger-tip'].rotation.x;
        lIndexObj.rotation.y = leftHand.joints['index-finger-tip'].rotation.y;
        lIndexObj.rotation.z = leftHand.joints['index-finger-tip'].rotation.z;
    }

    // Right Hand Joint References
    if (rightHand && rightHand.joints && rightHand.joints['thumb-tip']) {
        rThumbObj.position.x = rightHand.joints['thumb-tip'].position.x;
        rThumbObj.position.y = rightHand.joints['thumb-tip'].position.y;
        rThumbObj.position.z = rightHand.joints['thumb-tip'].position.z;
        rThumbObj.rotation.x = rightHand.joints['thumb-tip'].rotation.x;
        rThumbObj.rotation.y = rightHand.joints['thumb-tip'].rotation.y;
        rThumbObj.rotation.z = rightHand.joints['thumb-tip'].rotation.z;
    }

    if (rightHand && rightHand.joints && rightHand.joints['index-finger-tip']) {
        rIndexObj.position.x = rightHand.joints['index-finger-tip'].position.x;
        rIndexObj.position.y = rightHand.joints['index-finger-tip'].position.y;
        rIndexObj.position.z = rightHand.joints['index-finger-tip'].position.z;
        rIndexObj.rotation.x = rightHand.joints['index-finger-tip'].rotation.x;
        rIndexObj.rotation.y = rightHand.joints['index-finger-tip'].rotation.y;
        rIndexObj.rotation.z = rightHand.joints['index-finger-tip'].rotation.z;
    }

    // Left Hand Pinch Detection
    const lIndexThumbDist = lIndexObj.position.distanceTo(lThumbObj.position);

    if (lIndexThumbDist < 0.02 && !isGameOver) {
        lPinchSphere.position.x = lThumbObj.position.x;
        lPinchSphere.position.y = lThumbObj.position.y;
        lPinchSphere.position.z = lThumbObj.position.z;
        
        // Sphere movement interaction - start movement when left hand pinches
        if (!leftHandPinchActive) {
            leftHandPinchActive = true;
            lastLeftHandX = lThumbObj.position.x;
        }
        
        // Calculate horizontal movement for sphere
        if (leftHandPinchActive) {
            const currentLeftHandX = lThumbObj.position.x;
            const deltaX = currentLeftHandX - lastLeftHandX;
                   
            // Move sphere based on horizontal movement
            if (Math.abs(deltaX) > 0.001) {
                const maxX = halfW - currentFruitRadius;  // keep inside walls by its radius
                const newX = dropSphere.position.x + deltaX * sphereMovementSpeed;
                dropSphere.position.x = THREE.MathUtils.clamp(newX, -maxX, maxX);
            }
            
            lastLeftHandX = currentLeftHandX;
        }
    } else {
        // Stop sphere movement when left hand stops pinching
        if (leftHandPinchActive) {
            leftHandPinchActive = false;
            // Spawn cube at the dropSphere location when released
            const spawnPosition = new THREE.Vector3();
            dropSphere.getWorldPosition(spawnPosition);
            spawnSphere(spawnPosition);
        }
    }

    // Right Hand Pinch Detection
    const rIndexThumbDist = rIndexObj.position.distanceTo(rThumbObj.position);
    
    if (rIndexThumbDist < 0.02 && !isGameOver) {
        rPinchSphere.position.x = rThumbObj.position.x;
        rPinchSphere.position.y = rThumbObj.position.y;
        rPinchSphere.position.z = rThumbObj.position.z;
        
        // Sphere movement interaction - start movement when right hand pinches
        if (!rightHandPinchActive) {
            rightHandPinchActive = true;
            lastRightHandX = rThumbObj.position.x;
        }
        
        // Calculate horizontal movement for sphere
        if (rightHandPinchActive) {
            const currentRightHandX = rThumbObj.position.x;
            const deltaX = currentRightHandX - lastRightHandX;

            // Move sphere based on horizontal movement
            if (Math.abs(deltaX) > 0.001) {
                const maxX = halfW - currentFruitRadius;  // keep inside walls by its radius
                const newX = dropSphere.position.x + deltaX * sphereMovementSpeed;
                dropSphere.position.x = THREE.MathUtils.clamp(newX, -maxX, maxX);
            }
            
            lastRightHandX = currentRightHandX;
        }
    } else {
        // Stop sphere movement when right hand stops pinching
        if (rightHandPinchActive) {
            rightHandPinchActive = false;
            // Spawn cube at the dropSphere location when released
            const spawnPosition = new THREE.Vector3();
            dropSphere.getWorldPosition(spawnPosition);
            spawnSphere(spawnPosition);
        }
    }
}

// Function to check if index finger is pressing the button
function checkButtonPress() {
    // Only check button press when game is over and button is visible
    if (!isGameOver || endPanel.scale.x === 0) return;
    
    // Get button world position and size
    const buttonWorldPos = new THREE.Vector3();
    buttonPlaceholder.getWorldPosition(buttonWorldPos);
    
    // Get button dimensions (in world space)
    // buttonPlaceholder is 0.2 x 0.06 in size
    const buttonWidth = 0.2 * endPanel.scale.x;
    const buttonHeight = 0.06 * endPanel.scale.y;
    const buttonDepth = 0.03; // Touch detection depth
    
    // Get button's forward direction (normal to the button face)
    const buttonNormal = new THREE.Vector3(0, 0, 1);
    buttonNormal.applyQuaternion(buttonPlaceholder.getWorldQuaternion(new THREE.Quaternion()));
    
    // Check left index finger
    const leftIndexWorldPos = new THREE.Vector3(
        lIndexObj.position.x,
        lIndexObj.position.y,
        lIndexObj.position.z
    );
    
    // Check right index finger
    const rightIndexWorldPos = new THREE.Vector3(
        rIndexObj.position.x,
        rIndexObj.position.y,
        rIndexObj.position.z
    );
    
    // Helper function to check if finger is touching button
    function isFingerTouchingButton(fingerPos) {
        // Calculate vector from button center to finger
        const toFinger = fingerPos.clone().sub(buttonWorldPos);
        
        // Project onto button's local space
        const buttonRight = new THREE.Vector3(1, 0, 0);
        buttonRight.applyQuaternion(buttonPlaceholder.getWorldQuaternion(new THREE.Quaternion()));
        const buttonUp = new THREE.Vector3(0, 1, 0);
        buttonUp.applyQuaternion(buttonPlaceholder.getWorldQuaternion(new THREE.Quaternion()));
        
        const localX = toFinger.dot(buttonRight);
        const localY = toFinger.dot(buttonUp);
        const localZ = toFinger.dot(buttonNormal);
        
        // Check if finger is within button bounds
        const withinX = Math.abs(localX) <= buttonWidth / 2;
        const withinY = Math.abs(localY) <= buttonHeight / 2;
        const withinZ = localZ >= -0.005 && localZ <= buttonDepth; // In front of button
        
        return withinX && withinY && withinZ;
    }
    
    const leftIndexTouching = isFingerTouchingButton(leftIndexWorldPos);
    const rightIndexTouching = isFingerTouchingButton(rightIndexWorldPos);
    
    // Handle left index press
    if (leftIndexTouching && !buttonPressedByLeftIndex) {
        buttonPressedByLeftIndex = true;
        onButtonPress();
    } else if (!leftIndexTouching && buttonPressedByLeftIndex) {
        buttonPressedByLeftIndex = false;
        onButtonRelease();
    }
    
    // Handle right index press
    if (rightIndexTouching && !buttonPressedByRightIndex) {
        buttonPressedByRightIndex = true;
        onButtonPress();
    } else if (!rightIndexTouching && buttonPressedByRightIndex) {
        buttonPressedByRightIndex = false;
        onButtonRelease();
    }
}

// Function called when button is pressed
function onButtonPress() {
    if (buttonPressed) return; // Already pressed
    
    // Check cooldown
    const currentTime = Date.now();
    if (currentTime - lastButtonPressTime < BUTTON_COOLDOWN) {
        console.log('Button on cooldown');
        return;
    }
    
    buttonPressed = true;
    lastButtonPressTime = currentTime;
    console.log('Button pressed!');
    
    // GSAP animation: Scale down to 70%
    gsap.to(buttonPlaceholder.scale, {
        x: 0.7,
        y: 0.7,
        z: 0.7,
        duration: 0.3,
        ease: "power2.out"
    });
}

// Function called when button is released
function onButtonRelease() {
    // Only release if no fingers are touching
    if (buttonPressedByLeftIndex || buttonPressedByRightIndex) return;
    
    if (!buttonPressed) return; // Already released
    
    buttonPressed = false;
    console.log('Button released!');
    
    // GSAP animation: Scale back up to 100% with 0.2s delay
    gsap.to(buttonPlaceholder.scale, {
        x: 1,
        y: 1,
        z: 1,
        duration: 0.3,
        delay: 0.2,
        ease: "power2.out"
    });
    
    // Restart game after 0.3 seconds
    setTimeout(() => {
        // Scale down endPanel
        gsap.to(endPanel.scale, {
            x: 0,
            y: 0,
            z: 0,
            duration: 0.3,
            ease: "back.in(1.7)",
            onComplete: () => {
                restartGame();
            }
        });
    }, 300); // 0.3 seconds delay
}

// Function to restart the game
function restartGame() {
    console.log('=== RESTARTING GAME ===');
    
    // Convert meshes map to array for GSAP stagger animation
    const meshArray = Array.from(fruitMeshes.values());
    
    if (meshArray.length > 0) {
        // Animate all fruits scaling down with stagger effect
        gsap.to(meshArray.map(mesh => mesh.scale), {
            x: 0,
            y: 0,
            z: 0,
            duration: 0.2,
            stagger: 0.01, // 0.01 second delay between each fruit
            ease: "power2.in",
            onComplete: () => {
                // After all animations complete, clean up the meshes
                cleanupAndResetGame();
            }
        });
    } else {
        // No fruits to animate, just reset immediately
        cleanupAndResetGame();
    }
}

// Function to clean up meshes and reset game state
function cleanupAndResetGame() {
    // Clean the container - remove all fruit meshes
    fruitMeshes.forEach((mesh, id) => {
        containerModel.remove(mesh);
        
        // Properly dispose of all geometries and materials (handles GLB models)
        mesh.traverse((child) => {
            if (child.geometry) child.geometry.dispose();
            if (child.material) {
                if (Array.isArray(child.material)) {
                    child.material.forEach(mat => mat.dispose());
                } else {
                    child.material.dispose();
                }
            }
        });
    });
    fruitMeshes.clear();
    
    // Tell physics worker to clear all bodies
    physics.postMessage({ type: 'clear' });
    
    // Reset game state
    isGameOver = false;
    columnCoverageStartTime = null;
    
    // Reset score
    score = 0;
    comboCount = 0;
    lastMergeTime = 0;
    lastDisplayedScore = -1; // Reset cache to force display update
    updateScoreDisplay();
    
    // Reset time
    gameStartTime = Date.now();
    elapsedSeconds = 0;
    lastDisplayedTime = ''; // Reset cache to force display update
    updateTimeDisplay();
    
    // Reset fruit types
    currentFruitType = getRandomSpawnableFruit();
    currentFruitRadius = FRUITS[currentFruitType].radius;
    nextFruitType = getRandomSpawnableFruit();
    nextFruitRadius = FRUITS[nextFruitType].radius;
    
    // Update drop sphere and preview
    updateDropSphere();
    updateNextFruitPreview();
    
    // Show drop sphere and vertical line again
    dropSphere.visible = true;
    verticalLine.visible = true;
    verticalLine.scale.set(1, 1, 1); // Reset scale
    // Note: dropSphere scale is already correct from updateDropSphere()
    
    // Reset drop sphere position to center
    dropSphere.position.x = 0;
    
    console.log('Game restarted successfully!');
}

// Function to spawn a sphere at given position
function spawnSphere(worldPos) {
    // Prevent spawning if game is over
    if (isGameOver) {
        console.log('Cannot spawn - Game Over');
        return;
    }
    
    const currentTime = Date.now();
    
    // Prevent spawning for 0.5 seconds after entering XR mode
    if (xrSessionStartTime && currentTime - xrSessionStartTime < 500) {
        return;
    }
    
    if (currentTime - lastSpawnTime < spawnCooldown) {
        return;
    }
    lastSpawnTime = currentTime;

    // Immediately hide dropSphere and vertical line when fruit is released
    dropSphere.visible = false;
    verticalLine.visible = false;

    // Convert to container-local
    const local = worldToContainer(worldPos);
    // Get fruit config for physics properties
    const fruitConfig = getFruitConfig(currentFruitType);
    // Ask worker to spawn a physics circle with current fruit type
    physics.postMessage({ 
        type: 'spawn', 
        x: local.x, 
        y: local.y, 
        radius: currentFruitRadius,
        fruitType: currentFruitType,
        restitution: fruitConfig.restitution
    });
    
    // Rotate fruits: next becomes current, generate new next
    currentFruitType = nextFruitType;
    currentFruitRadius = nextFruitRadius;
    nextFruitType = getRandomSpawnableFruit();
    nextFruitRadius = FRUITS[nextFruitType].radius;
    
    // Update both displays (while hidden)
    updateDropSphere();
    updateNextFruitPreview();
    
    // After 0.6 seconds: Show the NEW fruit and scale up from 0 to target scale
    setTimeout(() => {
        // Don't show if game is over
        if (isGameOver) {
            return;
        }
        
        // Store the target scale (the correct model scale)
        const targetScale = dropSphere.scale.x;
        
        // Set scale to 0 before showing
        dropSphere.scale.set(0, 0, 0);
        verticalLine.scale.set(0, 0, 0);
        
        // Make visible
        dropSphere.visible = true;
        verticalLine.visible = true;
        
        // Animate scale from 0 to the correct target scale (not 1!)
        gsap.to(dropSphere.scale, 
            { x: targetScale, y: targetScale, z: targetScale, duration: 0.2, ease: "power2.out" }
        );
        
        // Animate vertical line from scale 0 to 1
        gsap.to(verticalLine.scale, 
            { x: 1, y: 1, z: 1, duration: 0.2, ease: "power2.out" }
        );
    }, 600);
}

// Update vertical line and cloudPlaceholder to follow dropSphere
function updateVerticalLine() {
    if (verticalLine && dropSphere) {
        // Update X position to match dropSphere
        verticalLine.position.x = dropSphere.position.x;
        
        // Sync visibility with dropSphere
        verticalLine.visible = dropSphere.visible;
    }
    
    if (cloudPlaceholder && dropSphere) {
        // Update X position to match dropSphere
        cloudPlaceholder.position.x = dropSphere.position.x;
        
        // cloudPlaceholder stays always visible
    }
}

// GSAP Animations Function
function GSAPAnims() {
    // Add your GSAP animations here
    // Example: You can animate objects, camera, or any properties
    // gsap.to(someObject.position, { x: 1, duration: 1, ease: "power2.inOut" });
}

renderer.setAnimationLoop(function () {
    delta = clock.getDelta();
    gsap.ticker.tick(delta);
    
    // NEW: advance physics in worker
    physics.postMessage({ type: 'step', dt: delta, substeps: 1 });
    
    // Update time display
    updateTimeDisplay();
    
    // Update vertical line position to follow dropSphere
    updateVerticalLine();
    
    // Pulse cloud scale between 0.95x and 1.0x
    if (cloudPlaceholder) {
        const time = clock.getElapsedTime();
        const pulseSpeed = 9.0; // Adjust for faster/slower pulsing
        const scale = 0.05 * (0.975 + 0.025 * Math.sin(time * pulseSpeed));
        cloudPlaceholder.scale.setScalar(scale);
    }
    
    renderer.render(scene, camera);
    handTracking();
    checkButtonPress();
    positionCompositionAtHeadHeight();
    
    GSAPAnims();
});